import type { SupervisorButton } from '../types/supervisorButtons';
import type { QuotaQueueInfo } from '../utils/quotaQueue';

import axiosInstance from './axiosInstance';
import { normalizeApiError } from './apiError';
import type { DeveloperStatusStep, WebSearchActivity } from './chatService';
import { readPreferredModel } from './modelCatalog';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const toApiAssetUrl = (url?: string | null): string | null => {
  if (!url) return null;
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  if (url.startsWith('/')) return `${API_BASE_URL}${url}`;

  return url;
};

export type VcsStatus = 'pending' | 'syncing' | 'ok' | 'skipped' | 'token_invalid' | 'failed';

export interface Project {
  id: string;
  title: string | null;
  description: string | null;
  status: string;
  is_public?: boolean;
  code_ready?: boolean;
  vcs_status?: VcsStatus;
  has_agent_repository?: boolean;
  title_confirmed_at?: string | null;
  title_changed_by_user_at?: string | null;
  role?: string | null;
  is_owner?: boolean;
  created_at: string;
  updated_at: string;
}

/** A prepared initial Start that is durably waiting for project-title confirmation. */
export interface PendingTitleConfirmation {
  invocation_id: string;
  source_entry_id: string;
  session_id: string | null;
  planning_run_id: string;
  button_key: string;
  base_label: string;
  displayed_label: string;
  planning_selection: PlanningActionSelectionInput;
  agents: string[];
}

export interface ProjectRenameRequest {
  title: string;
  expected_title: string;
  request_id: string;
  session_id?: string;
}

export interface ProjectRenameResult {
  project: Project;
  task_id: string | null;
}

export interface BuildStatus {
  batch_id: string;
  task_ids?: string[];
  status: string;
  completed: number;
  total: number;
  paused?: number;
  created_at?: string;
  completed_at?: string;
  eta_seconds: number | null;
}

export interface GitHubConnectionStatus {
  connected: boolean;
  repo_url: string | null;
  username: string | null;
  source_repo_url?: string | null;
  mirror_repo_url?: string | null;
  default_branch?: string | null;
  has_agent_repository?: boolean;
}

export type GitHubSyncDirection = 'source_to_mirror' | 'mirror_to_source';

export interface GitHubConnectPayload {
  repo_url: string;
  username?: string;
  access_token?: string;
  title?: string;
  create_repo_if_missing?: boolean;
  sync_now?: boolean;
}

export interface GitHubRepositorySummary {
  full_name: string;
  html_url: string;
  clone_url: string;
  private: boolean;
  default_branch?: string | null;
  permissions?: Record<string, boolean> | null;
}

export interface RepositoryLinkResponse {
  registered: boolean;
  remote_url: string;
  normalized_remote_url: string;
  project: UserProject | null;
}

interface Message {
  id: string;
  user_id: string;
  project_id?: string | null;
  session_id?: string | null;
  content: string;
  media_urls?: string[] | null;
  media_metadata?: Record<string, unknown>[] | null;
  prompt_scope?: Record<string, unknown> | null;
  author: string;
  sender_name?: string | null;
  sender_email?: string | null;
  created_at: string;
  updated_at: string;
  phase?: string | null;
  allowed_buttons?: string[] | null;
  buttons?: SupervisorButton[] | null;
  clicked_button?: string | null;
  clicked_buttons?: string[] | null;
  progress?: number | null;
  developer_status_steps?: DeveloperStatusStep[];
  developer_job_id?: string | null;
  developer_status?: string | null;
  tool_activity?: WebSearchActivity[] | null;
}

interface ProjectDetailResponse {
  project: Project;
  messages: Message[];
  pending_suggested_agents?: {
    agents: string[];
    buttons?: SupervisorButton[];
    message_id: string;
    session_id?: string | null;
    keep_talking?: boolean;
    active?: boolean;
    prompt_scope?: Record<string, unknown> | null;
    ai_content?: string | null;
  } | null;
  pending_title_confirmation?: PendingTitleConfirmation | null;
}

export interface UserProject {
  id: string;
  title: string;
  role: string;
  is_owner: boolean;
  access_status?: 'accepted' | 'pending';
  access_source?: string | null;
  task_count?: number;
  screenshot_thumb_url?: string | null;
  screenshot_page_name?: string | null;
}

export interface ProjectWithDetails {
  id: string;
  title: string;
  first_message: string | null;
  status: string;
  role: string;
  is_owner: boolean;
  is_public: boolean;
  task_count: number;
  is_extreme_design?: boolean;
  created_at: string | null;
  updated_at: string | null;
  access_status?: 'accepted' | 'pending';
  access_source?: string | null;
}

export interface PaginatedProjectsResponse {
  items: ProjectWithDetails[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface ExploreProjectItem {
  id: string;
  title: string;
  first_message: string | null;
  status: string;
  owner_name: string | null;
  owner_image_url: string | null;
  is_extreme_design?: boolean;
  votes: { up: number; down: number };
  created_at: string | null;
  updated_at: string | null;
}


export type EnvDataType = 'string' | 'int' | 'boolean' | 'float' | 'list' | 'dict' | 'json';

export interface ProjectEnvironmentVariable {
  id: string;
  project_id: string;
  key: string;
  value: string | null;
  data_type: EnvDataType;
  environment: string;
  version: number;
}

export type EnvVarUpdate = {
  key: string;
  value: string | null;
  data_type: string;
  environment?: string;
};

export type ProjectCodeDownload = {
  blob: Blob;
  filename: string | null;
};

const getAttachmentFilename = (contentDisposition?: string): string | null => {
  const match = contentDisposition?.match(/filename="?([^"]+)"?/i);

  return match?.[1] || null;
};

export interface ActionBarClick {
  id: string;
  project_id: string;
  entry_id: string;
  phase: string;
  button_key: string;
  clicked_at: string;
}

export interface ActionBarAppearance {
  id: string;
  project_id: string;
  entry_id: string;
  phase: string;
  available_buttons: Array<string | SupervisorButton>;
  shown_at: string;
}

export interface PlanningActionSelectionInput {
  task_ids: string[];
  stages: string[];
}

export interface ActionBarInvocation {
  id: string;
  project_id: string;
  session_id?: string | null;
  content: string;
  author: string;
  created_at: string;
  planning_run_id?: string | null;
  propagation_id?: string | null;
}

export interface RestoreSnapshot {
  type: 'ai_overview_log' | 'artifact' | 'chat_message';
  log?: Record<string, unknown>;
  artifact?: { artifact_type: string; rows: Record<string, unknown>[] } | null;
  artifact_type?: string;
  rows?: Record<string, unknown>[];
  logs?: Record<string, unknown>[];
  message?: Record<string, unknown>;
  /** Action-bar clicks the revert cleared so the gate's pills (Reviewed /
   *  Continued / etc.) can re-activate after Undo. Restored alongside the
   *  artifact rows on Redo. */
  action_bar_clicks?: Record<string, unknown>[];
}

export interface DeleteWithSnapshotResponse {
  deleted: boolean;
  snapshot?: RestoreSnapshot | null;
}

export interface BuildScope {
  sprintId?: string | null;
  phaseId?: string | null;
  taskIds?: string[];
  /** Explicit subset of the current phase. Undefined keeps legacy whole-phase behavior. */
  selectedPhaseTaskIds?: string[];
  /** Completed AI summary version for this exact phase-task selection. */
  selectionSummaryId?: string;
}

export type AgentModelTier =
  | 'fast'
  | 'spark'
  | 'standard'
  | 'large'
  | 'super_large'
  | 'byok_standard'
  | 'byok_large'
  | 'byok_super_large';

export interface ProjectAgentPreference {
  enabled: boolean;
  model_tier: AgentModelTier;
}

export interface ProjectAgentsResponse {
  project_id: string;
  agents: Record<string, ProjectAgentPreference>;
}

export type ProjectCreditBillingSource = 'own_credit' | 'org_credit';
export type ProjectCreditEffectiveBillingSource = ProjectCreditBillingSource | 'unlimited_personal' | 'unlimited_organization';

export interface ProjectCreditSourceStatus {
  project_id: string;
  user_id: string;
  is_org_project: boolean;
  can_use_org_credit: boolean;
  credit_billing_source: ProjectCreditEffectiveBillingSource;
  organization_id?: string | null;
  organization_name?: string | null;
  org_credits_balance: number;
  own_credits_balance: number;
  is_unlimited?: boolean;
  unlimited_scope?: 'personal' | 'organization' | null;
}

export interface ChatSessionSummary {
  id: string;
  user_id?: string | null;
  project_id: string;
  mode?: string;
  is_developer_mode?: boolean;
  name: string | null;
  description: string | null;
  auto_rename_enabled?: boolean;
  auto_renamed_at?: string | null;
  creator_name?: string | null;
  creator_image_url?: string | null;
  is_current_user?: boolean;
  locked_by_user_id?: string | null;
  locked_by_name?: string | null;
  locked_by_image_url?: string | null;
  locked_at?: string | null;
  lock_reason?: string | null;
  is_locked?: boolean;
  is_locked_by_current_user?: boolean;
  can_edit?: boolean;
  can_delete?: boolean;
  can_lock?: boolean;
  can_send_message?: boolean;
  is_read_only?: boolean;
  read_only_reason?: string | null;
  last_message_content: string | null;
  last_message_created_at?: string | null;
  input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export type ChatSessionScope = 'mine' | 'all';

const normalizeChatSession = (session: ChatSessionSummary): ChatSessionSummary => ({
  ...session,
  creator_image_url: toApiAssetUrl(session.creator_image_url),
  locked_by_image_url: toApiAssetUrl(session.locked_by_image_url),
});

export interface ChatSessionCreatePayload {
  name?: string | null;
  description?: string | null;
  mode?: 'standard' | 'developer';
}

export const projectService = {
  async getProjectAgents(projectId: string): Promise<ProjectAgentsResponse> {
    const response = await axiosInstance.get(`/api/v1/projects/${projectId}/agents`);

    return response.data;
  },

  async updateProjectAgents(
    projectId: string,
    agents: Record<string, ProjectAgentPreference>,
  ): Promise<ProjectAgentsResponse> {
    const response = await axiosInstance.put(`/api/v1/projects/${projectId}/agents`, { agents });

    return response.data;
  },

  async getProjectCreditSource(projectId: string): Promise<ProjectCreditSourceStatus> {
    const response = await axiosInstance.get(`/api/v1/projects/${projectId}/credit-source`);

    return response.data;
  },

  async updateProjectCreditSource(
    projectId: string,
    creditBillingSource: ProjectCreditBillingSource,
  ): Promise<ProjectCreditSourceStatus> {
    const response = await axiosInstance.put(`/api/v1/projects/${projectId}/credit-source`, {
      credit_billing_source: creditBillingSource,
    });

    return response.data;
  },

  async getChatSessions(
    projectId: string,
    scope: ChatSessionScope = 'mine',
  ): Promise<ChatSessionSummary[]> {
    const response = await axiosInstance.get(`/api/v1/projects/${projectId}/chat-sessions`, {
      params: { scope },
    });

    return (response.data?.sessions ?? []).map(normalizeChatSession);
  },

  async createChatSession(
    projectId: string,
    payload: ChatSessionCreatePayload = {},
  ): Promise<ChatSessionSummary> {
    const response = await axiosInstance.post(`/api/v1/projects/${projectId}/chat-sessions`, payload);

    return normalizeChatSession(response.data);
  },

  async updateChatSession(
    projectId: string,
    sessionId: string,
    payload: ChatSessionCreatePayload,
  ): Promise<ChatSessionSummary> {
    const response = await axiosInstance.patch(
      `/api/v1/projects/${projectId}/chat-sessions/${sessionId}`,
      payload,
    );

    return normalizeChatSession(response.data);
  },

  async deleteChatSession(projectId: string, sessionId: string): Promise<void> {
    await axiosInstance.delete(`/api/v1/projects/${projectId}/chat-sessions/${sessionId}`);
  },

  async lockChatSession(projectId: string, sessionId: string): Promise<ChatSessionSummary> {
    const response = await axiosInstance.post(`/api/v1/projects/${projectId}/chat-sessions/${sessionId}/lock`);

    return normalizeChatSession(response.data);
  },

  async unlockChatSession(projectId: string, sessionId: string): Promise<ChatSessionSummary> {
    const response = await axiosInstance.delete(`/api/v1/projects/${projectId}/chat-sessions/${sessionId}/lock`);

    return normalizeChatSession(response.data);
  },

  async getAiOverviewLogs(
    projectId: string,
    opts?: { limit?: number; before?: string; sessionId?: string | null },
  ): Promise<{
    logs: {
      id: string;
      project_id: string;
      session_id?: string | null;
      agent_type: string;
      content: string;
      parent_id: string | null;
      run_group: string;
      artifact_type?: string | null;
      page?: string | null;
      created_at: string;
      phase?: string | null;
      allowed_buttons?: string[] | null;
      buttons?: SupervisorButton[] | null;
      clicked_button?: string | null;
      clicked_buttons?: string[] | null;
      artifact_count?: number | null;
    }[];
    next_cursor: string | null;
    has_more: boolean;
    seen_counts?: Record<string, number>;
    read_message_ids?: string[];
  }> {
    const params: Record<string, string | number> = {};

    if (opts?.limit !== undefined) params.limit = opts.limit;
    if (opts?.before) params.before = opts.before;
    if (opts?.sessionId) params.session_id = opts.sessionId;
    const response = await axiosInstance.get(
      `/api/v1/projects/${projectId}/ai-overview`,
      { params },
    );

    return response.data;
  },

  async deleteAiOverviewEntry(projectId: string, entryId: string): Promise<DeleteWithSnapshotResponse> {
    const response = await axiosInstance.delete(`/api/v1/projects/${projectId}/ai-overview/${entryId}`);

    return response.data;
  },

  async deleteAllAiOverview(projectId: string): Promise<void> {
    await axiosInstance.delete(`/api/v1/projects/${projectId}/ai-overview`);
  },

  async getReadMessageIds(projectId: string): Promise<string[]> {
    const response = await axiosInstance.get(`/api/v1/projects/${projectId}/ai-overview/read`);

    return response.data?.read_message_ids || [];
  },

  async markMessagesRead(projectId: string, messageIds: string[]): Promise<void> {
    await axiosInstance.put(`/api/v1/projects/${projectId}/ai-overview/read`, {
      message_ids: messageIds,
    });
  },

  async revertArtifact(projectId: string, artifactType: string): Promise<DeleteWithSnapshotResponse> {
    const response = await axiosInstance.delete(
      `/api/v1/projects/${projectId}/ai-overview/revert/${artifactType}`,
    );

    return response.data;
  },

  async restoreItem(projectId: string, snapshot: RestoreSnapshot): Promise<void> {
    await axiosInstance.post(`/api/v1/projects/${projectId}/ai-overview/restore-item`, snapshot);
  },

  async getRedoStack(projectId: string): Promise<{ entries: Record<string, unknown>[]; count: number }> {
    const response = await axiosInstance.get(`/api/v1/projects/${projectId}/redo-snapshot`);

    return {
      entries: response.data?.entries ?? [],
      count: response.data?.count ?? 0,
    };
  },

  async pushRedoSnapshot(projectId: string, entry: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await axiosInstance.put(`/api/v1/projects/${projectId}/redo-snapshot`, entry);

    return response.data?.entry ?? entry;
  },

  async popRedoSnapshot(projectId: string, snapshotId?: string): Promise<void> {
    const params = snapshotId ? `?snapshot_id=${encodeURIComponent(snapshotId)}` : '';

    await axiosInstance.delete(`/api/v1/projects/${projectId}/redo-snapshot${params}`);
  },

  async getActionBarClicks(projectId: string, sessionId?: string | null): Promise<ActionBarClick[]> {
    const response = await axiosInstance.get(
      `/api/v1/projects/${projectId}/action-bar-clicks`,
      { params: sessionId ? { session_id: sessionId } : undefined },
    );

    return response.data ?? [];
  },

  async recordActionBarClick(
    projectId: string,
    entryId: string,
    phase: string,
    buttonKey: string,
    sessionId?: string | null,
  ): Promise<ActionBarClick> {
    const response = await axiosInstance.post(
      `/api/v1/projects/${projectId}/action-bar-clicks`,
      { entry_id: entryId, phase, button_key: buttonKey, session_id: sessionId ?? null },
    );

    return response.data;
  },

  async recordActionBarInvocation(
    projectId: string,
    entryId: string | undefined,
    phase: string,
    buttonKey: string,
    displayedLabel: string,
    invocationId: string,
    sessionId?: string | null,
    selection?: { planningSelection: PlanningActionSelectionInput; baseLabel: string },
  ): Promise<ActionBarInvocation> {
    const response = await axiosInstance.post(
      `/api/v1/projects/${projectId}/action-bar-invocations`,
      {
        entry_id: entryId ?? null,
        phase,
        button_key: buttonKey,
        session_id: sessionId ?? null,
        invocation_id: invocationId,
        displayed_label: displayedLabel,
        ...(selection ? { planning_selection: selection.planningSelection, base_label: selection.baseLabel } : {}),
      },
    );

    return response.data;
  },

  async runArtifactUpdates(projectId: string, planningActionInvocationId: string): Promise<unknown> {
    const response = await axiosInstance.post(`/api/v1/projects/${projectId}/artifact-updates/run`, {
      planning_action_invocation_id: planningActionInvocationId,
    });

    return response.data;
  },

  /** Close an offer's unfinished updates and keep those artifacts exactly as they are. */
  async keepCurrentArtifacts(projectId: string, entryId: string, taskIds: string[]): Promise<unknown> {
    const response = await axiosInstance.post(`/api/v1/projects/${projectId}/artifact-updates/keep-current`, {
      entry_id: entryId,
      task_ids: taskIds,
    });

    return response.data;
  },

  async preparePlanningTaskPreview(
    projectId: string,
    entryId: string,
    phase = 'agent_suggestion',
    buttonKey = 'start',
  ): Promise<{ planning_run_id: string; task_ids: string[] }> {
    const response = await axiosInstance.post(`/api/v1/projects/${projectId}/planning-task-preview`, {
      entry_id: entryId, phase, button_key: buttonKey, session_id: null,
    });

    return response.data;
  },

  async getActionBarAppearances(projectId: string, sessionId?: string | null): Promise<ActionBarAppearance[]> {
    const response = await axiosInstance.get(
      `/api/v1/projects/${projectId}/action-bar-appearances`,
      { params: sessionId ? { session_id: sessionId } : undefined },
    );

    return response.data ?? [];
  },

  async recordActionBarAppearance(
    projectId: string,
    entryId: string,
    phase: string,
    availableButtons: string[],
    sessionId?: string | null,
  ): Promise<ActionBarAppearance> {
    const response = await axiosInstance.post(
      `/api/v1/projects/${projectId}/action-bar-appearances`,
      {
        entry_id: entryId,
        phase,
        available_buttons: availableButtons,
        session_id: sessionId ?? null,
      },
    );

    return response.data;
  },

  async clearRedoStack(projectId: string): Promise<void> {
    await axiosInstance.delete(`/api/v1/projects/${projectId}/redo-snapshot?all=true`);
  },

  async getProjects(): Promise<Project[]> {
    const response = await axiosInstance.get('/api/v1/projects/');

    return response.data;
  },

  async getProject(projectId: string, opts?: { sessionId?: string | null }): Promise<ProjectDetailResponse> {
    const response = await axiosInstance.get(`/api/v1/projects/${projectId}`, {
      params: {
        ...(opts?.sessionId ? { session_id: opts.sessionId } : {}),
      },
    });

    return response.data;
  },

  async getDeveloperModeStatusHistory(
    projectId: string,
    opts?: { sessionId?: string | null; jobId?: string | null; limit?: number },
  ): Promise<DeveloperStatusStep[]> {
    const response = await axiosInstance.get(`/api/v1/projects/${projectId}/developer-mode/status-history`, {
      params: {
        ...(opts?.sessionId ? { session_id: opts.sessionId } : {}),
        ...(opts?.jobId ? { job_id: opts.jobId } : {}),
        ...(opts?.limit ? { limit: opts.limit } : {}),
      },
    });

    return response.data ?? [];
  },

  async checkTitleAvailability(
    title: string,
    excludeProjectId: string,
  ): Promise<{ available: boolean; reason: string | null }> {
    const response = await axiosInstance.get('/api/v1/projects/title-availability', {
      params: { title, exclude_project_id: excludeProjectId },
    });

    return response.data;
  },

  async confirmTitle(projectId: string, title: string, sessionId?: string | null): Promise<Project> {
    const response = await axiosInstance.put(`/api/v1/projects/${projectId}/confirm-title`, {
      title, ...(sessionId ? { session_id: sessionId } : {}),
    });

    return response.data;
  },

  async renameProject(projectId: string, request: ProjectRenameRequest): Promise<ProjectRenameResult> {
    return (await axiosInstance.post(`/api/v1/projects/${projectId}/rename`, request)).data;
  },

  async getMyProjects(): Promise<UserProject[]> {
    const response = await axiosInstance.get('/api/v1/projects/my');

    return response.data;
  },

  async getMyProjectSummary(projectId: string): Promise<UserProject> {
    const response = await axiosInstance.get(`/api/v1/projects/${projectId}/summary`);

    return response.data;
  },

  async getProjectsWithDetails(
    page: number = 1,
    limit: number = 12,
    search: string = '',
  ): Promise<PaginatedProjectsResponse> {
    const response = await axiosInstance.get('/api/v1/projects/with-details', {
      params: { page, limit, ...(search.trim() ? { search: search.trim() } : {}) },
    });

    return response.data;
  },

  async triggerBuild(
    projectId: string,
    defaultModel: string = 'super_large',
    forceExecutionTaskIds: string[] = [],
    scope: BuildScope = {},
  ): Promise<{
    status: string;
    queued_tasks: number;
    error_code?: string;
    error_message?: string;
    required_credits?: number;
    available_credits?: number;
    credit_shortfall?: number;
    phase_id?: string;
    phase_number?: number;
    phase_name?: string;
    warning_message?: string;
    backlogged_tasks?: number;
  }> {
    const response = await axiosInstance.post(`/api/v1/projects/${projectId}/build`, {
      default_model: defaultModel,
      model_id: readPreferredModel(),
      force_execution_task_ids: forceExecutionTaskIds,
      sprint_id: scope.sprintId ?? null,
      phase_id: scope.phaseId ?? null,
      task_ids: scope.taskIds ?? [],
      selected_phase_task_ids: scope.selectedPhaseTaskIds,
      selection_summary_id: scope.selectionSummaryId,
    });

    return response.data;
  },

  async triggerStageDeploy(projectId: string): Promise<{
    status: string;
    queued_tasks: number;
    error_code?: string;
    error_message?: string;
    required_credits?: number;
    available_credits?: number;
  }> {
    const response = await axiosInstance.post(`/api/v1/projects/${projectId}/deploy-stage`);

    return response.data;
  },

  /** Resume build: start a build held for its plan's hourly quota. Still spent, the chat gets the notice again. */
  async resumeHeldBuild(projectId: string): Promise<{ resumed: boolean; quota_queue: QuotaQueueInfo | null }> {
    const response = await axiosInstance.post(`/api/v1/projects/${projectId}/build/resume-held`);

    return response.data;
  },

  async cancelBuild(projectId: string): Promise<{ status: string }> {
    const response = await axiosInstance.post(`/api/v1/projects/${projectId}/cancel-build`);

    return response.data;
  },

  async cancelAgents(
    projectId: string,
    sessionId?: string | null,
  ): Promise<{ status: string; credits_refunded: number; cancelled_phase?: string | null }> {
    const response = await axiosInstance.post('/api/v1/chat/cancel-agents', {
      project_id: projectId,
      session_id: sessionId ?? null,
    });

    return response.data;
  },

  async getServicePorts(projectId: string): Promise<{
    service_name: string;
    hostname: string;
    port: number;
    environment?: string;
    preview_url?: string;
  }[]> {
    const response = await axiosInstance.get(`/api/v1/projects/${projectId}/ports`);

    return response.data;
  },

  async connectGitHub(
    projectId: string,
    data: GitHubConnectPayload
  ) {
    const response = await axiosInstance.post(`/api/v1/projects/${projectId}/github/connect`, data);

    return response.data;
  },

  async createProjectFromGitHub(data: GitHubConnectPayload): Promise<{
    status: string;
    project_id: string;
    source_repo_url?: string | null;
    mirror_repo_url?: string | null;
    default_branch?: string | null;
  }> {
    const response = await axiosInstance.post('/api/v1/projects/github/connect', data);

    return response.data;
  },

  async listGitHubRepositories(accessToken: string): Promise<GitHubRepositorySummary[]> {
    const response = await axiosInstance.post('/api/v1/projects/github/repositories', {
      access_token: accessToken,
    });

    return response.data.repositories || [];
  },

  async lookupRepositoryLink(remoteUrl: string): Promise<RepositoryLinkResponse> {
    const response = await axiosInstance.post('/api/v1/projects/repository-link/lookup', {
      remote_url: remoteUrl,
    });

    return response.data;
  },

  async getGitHubStatus(projectId: string): Promise<GitHubConnectionStatus> {
    const response = await axiosInstance.get(`/api/v1/projects/${projectId}/github/status`);

    return response.data;
  },

  async syncGitHub(
    projectId: string,
    data: { direction: GitHubSyncDirection; branch?: string; create_repo_if_missing?: boolean }
  ): Promise<{
    status: string;
    direction: GitHubSyncDirection;
    source_repo_url: string;
    mirror_repo_url: string;
    default_branch: string;
  }> {
    const response = await axiosInstance.post(`/api/v1/projects/${projectId}/github/sync`, data);

    return response.data;
  },

  async disconnectGitHub(projectId: string) {
    const response = await axiosInstance.delete(`/api/v1/projects/${projectId}/github/disconnect`);

    return response.data;
  },

  async getEnvVars(projectId: string, environment?: string): Promise<ProjectEnvironmentVariable[]> {
    const response = await axiosInstance.get(`/api/v1/projects/${projectId}/env-vars`, {
      params: environment ? { environment } : undefined,
    });

    return response.data;
  },

  async updateEnvVars(
    projectId: string,
    updates: EnvVarUpdate[],
    environment: string,
  ): Promise<ProjectEnvironmentVariable[]> {
    const response = await axiosInstance.put(
      `/api/v1/projects/${projectId}/env-vars`,
      updates.map((update) => ({
        key: update.key,
        value: update.value,
        data_type: update.data_type,
      })),
      { params: { environment } },
    );

    return response.data;
  },

  async deleteProject(projectId: string): Promise<void> {
    await axiosInstance.delete(`/api/v1/projects/${projectId}`);
  },

  async bulkDeleteProjects(projectIds: string[]): Promise<number> {
    const response = await axiosInstance.post('/api/v1/projects/bulk-delete', { project_ids: projectIds });

    return response.data.deleted_count;
  },

  async getBuildStatus(projectId: string): Promise<BuildStatus | null> {
    const response = await axiosInstance.get(`/api/v1/projects/${projectId}/build-status`);

    return response.data;
  },

  async getBuildHistory(projectId: string): Promise<BuildStatus[]> {
    const response = await axiosInstance.get(`/api/v1/projects/${projectId}/build-history`);

    return response.data;
  },

  async downloadProjectCode(projectId: string): Promise<ProjectCodeDownload> {
    const response = await axiosInstance.get(`/api/v1/projects/${projectId}/code/download`, {
      responseType: 'blob',
      timeout: 120000,
    });

    return {
      blob: response.data,
      filename: getAttachmentFilename(response.headers['content-disposition']),
    };
  },

  async getCodeServerUrl(projectId: string): Promise<{
    url: string;
    token: string;
    folder: string;
    session_id: string | null;
    heartbeat_interval_seconds?: number;
    heartbeat_ttl_seconds?: number;
  } | null> {
    try {
      const response = await axiosInstance.get(`/api/v1/projects/${projectId}/code-server`);

      return response.data;
    } catch (error) {
      if (normalizeApiError(error).status === 404) return null; // Not started yet.
      throw error;
    }
  },

  async startCodeServer(projectId: string): Promise<{
    url: string;
    token: string;
    folder: string;
    session_id: string | null;
    heartbeat_interval_seconds?: number;
    heartbeat_ttl_seconds?: number;
  }> {
    const response = await axiosInstance.post(`/api/v1/projects/${projectId}/code-server/start`);

    return response.data;
  },

  async heartbeatCodeServer(projectId: string, sessionId: string): Promise<{
    ok: boolean;
    status: string;
    credits_balance?: number;
    charged?: number;
    total_credits_charged?: number;
  }> {
    const response = await axiosInstance.post(
      `/api/v1/projects/${projectId}/code-server/${sessionId}/heartbeat`
    );

    return response.data;
  },

  async recoverRepo(projectId: string): Promise<{
    ok: boolean;
    page_count: number;
    url: string | null;
  }> {
    const response = await axiosInstance.post(`/api/v1/projects/${projectId}/recover-repo`);

    return response.data;
  },

  async getClusterDashboard(projectId: string): Promise<{
    url: string;
    token: string;
    namespace: string;
    bearer_token: string | null;
  } | null> {
    try {
      const response = await axiosInstance.get(`/api/v1/projects/${projectId}/cluster-dashboard`);

      return response.data;
    } catch (error) {
      if (normalizeApiError(error).status === 404) return null; // Not started yet.
      throw error;
    }
  },

  async startClusterDashboard(projectId: string): Promise<{
    url: string;
    token: string;
    namespace: string;
    bearer_token: string | null;
  }> {
    const response = await axiosInstance.post(
      `/api/v1/projects/${projectId}/cluster-dashboard/start`
    );

    return response.data;
  },

  async toggleVisibility(projectId: string, isPublic: boolean): Promise<{ is_public: boolean }> {
    const response = await axiosInstance.patch(`/api/v1/projects/${projectId}/visibility`, {
      is_public: isPublic,
    });

    return response.data;
  },

  async getPublicProject(projectId: string): Promise<{
    id: string;
    title: string | null;
    first_message: string | null;
    status: string;
    created_at: string;
    updated_at: string;
    owner_name: string | null;
    owner_image_url: string | null;
    votes: { up: number; down: number };
  }> {
    const response = await axiosInstance.get(`/api/v1/projects/${projectId}/public`);

    return response.data;
  },

  async castVote(projectId: string, voteType: 'up' | 'down'): Promise<{
    status: string;
    votes: { up: number; down: number };
  }> {
    const response = await axiosInstance.post(`/api/v1/projects/${projectId}/votes`, {
      vote_type: voteType,
    });

    return response.data;
  },

  async getVotes(projectId: string): Promise<{
    votes: { up: number; down: number };
    my_vote: string | null;
  }> {
    const response = await axiosInstance.get(`/api/v1/projects/${projectId}/votes`);

    return response.data;
  },

  async addComment(projectId: string, content: string): Promise<{
    id: string;
    user_id: string;
    username: string | null;
    content: string;
    created_at: string;
  }> {
    const response = await axiosInstance.post(`/api/v1/projects/${projectId}/comments`, {
      content,
    });

    return response.data;
  },

  async getComments(projectId: string): Promise<{
    id: string;
    user_id: string;
    username: string | null;
    content: string;
    created_at: string;
  }[]> {
    const response = await axiosInstance.get(`/api/v1/projects/${projectId}/comments`);

    return response.data;
  },

  async exploreProjects(page: number, limit: number = 12): Promise<{
    items: ExploreProjectItem[];
    total: number;
    page: number;
    limit: number;
    pages: number;
  }> {
    const response = await axiosInstance.get('/api/v1/projects/explore', {
      params: { page, limit },
    });

    return response.data;
  },

  async searchExploreProjects(query: string, page: number, limit: number = 12): Promise<{
    items: ExploreProjectItem[];
    total: number;
    page: number;
    limit: number;
    pages: number;
    query: string;
  }> {
    const response = await axiosInstance.get('/api/v1/projects/explore-search', {
      params: { q: query, page, limit },
      timeout: 30000,
    });

    return response.data;
  },

  async cloneProject(projectId: string): Promise<{
    project_id: string;
    cost: number;
    title: string | null;
    repository_status: VcsStatus;
  }> {
    const response = await axiosInstance.post(`/api/v1/projects/${projectId}/clone`);

    return response.data;
  },

  async resolveProject(identifier: string): Promise<{ id: string; title: string | null }> {
    const response = await axiosInstance.get(`/api/v1/projects/resolve/${encodeURIComponent(identifier)}`);

    return response.data;
  },

  async dismissSuggestions(projectId: string): Promise<void> {
    await axiosInstance.delete(`/api/v1/projects/${projectId}/suggested-agents`);
  },

  async queueDeveloperModeMessage(
    projectId: string,
    payload: { session_id: string; content: string; media_urls?: string[] },
  ): Promise<{
    id: string;
    project_id: string;
    session_id: string;
    chat_message_id: string | null;
    content: string;
    media_urls: string[];
    state: string;
    created_at: number;
    steered_at?: number | null;
  }> {
    const response = await axiosInstance.post(
      `/api/v1/projects/${projectId}/developer-mode/queued-messages`,
      payload,
    );

    return response.data;
  },

  async listQueuedDeveloperModeMessages(
    projectId: string,
    sessionId: string,
  ): Promise<Array<{
    id: string;
    session_id: string;
    content: string;
    state: string;
  }>> {
    const response = await axiosInstance.get(
      `/api/v1/projects/${projectId}/developer-mode/queued-messages`,
      { params: { session_id: sessionId } },
    );

    return response.data;
  },

  async steerDeveloperModeMessage(
    projectId: string,
    sessionId: string,
    queuedMessageId: string,
  ): Promise<{
    id: string;
    state: string;
  }> {
    const response = await axiosInstance.post(
      `/api/v1/projects/${projectId}/developer-mode/queued-messages/${queuedMessageId}/steer`,
      { session_id: sessionId },
    );

    return response.data;
  },

  async updateQueuedDeveloperModeMessage(
    projectId: string,
    sessionId: string,
    queuedMessageId: string,
    content: string,
  ): Promise<{
    id: string;
    content: string;
    state: string;
  }> {
    const response = await axiosInstance.patch(
      `/api/v1/projects/${projectId}/developer-mode/queued-messages/${queuedMessageId}`,
      { session_id: sessionId, content },
    );

    return response.data;
  },

  async deleteQueuedDeveloperModeMessage(
    projectId: string,
    sessionId: string,
    queuedMessageId: string,
  ): Promise<{
    id: string;
    state: string;
  }> {
    const response = await axiosInstance.delete(
      `/api/v1/projects/${projectId}/developer-mode/queued-messages/${queuedMessageId}`,
      { params: { session_id: sessionId } },
    );

    return response.data;
  },

  async reorderQueuedDeveloperModeMessages(
    projectId: string,
    sessionId: string,
    orderedIds: string[],
  ): Promise<Array<{
    id: string;
    session_id: string;
    content: string;
    state: string;
  }>> {
    const response = await axiosInstance.post(
      `/api/v1/projects/${projectId}/developer-mode/queued-messages/reorder`,
      { session_id: sessionId, ordered_ids: orderedIds },
    );

    return response.data;
  },

  async cancelDeveloperModeProcessing(
    projectId: string,
    payload: { session_id: string; job_id?: string | null },
  ): Promise<{ status: string; project_id: string; session_id: string; job_id?: string | null }> {
    const response = await axiosInstance.post(
      `/api/v1/projects/${projectId}/developer-mode/cancel`,
      payload,
    );

    return response.data;
  },
};
